/**
 * Branch List Component
 * Menampilkan daftar cabang dengan opsi CRUD
 * Owner only
 */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchBranchesAsync,
  deleteBranchAsync,
  setSelectedBranch,
} from './branchSlice';
import { Branch } from '../../types/branch';
import BranchFormModal from './BranchFormModal';

const BranchList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { branches, loading, error } = useSelector((state: RootState) => state.branch);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    dispatch(fetchBranchesAsync({ include_inactive: includeInactive }));
  }, [dispatch, includeInactive]);

  const handleDelete = async (branchId: number) => {
    if (window.confirm('Apakah Anda yakin ingin menonaktifkan cabang ini?')) {
      await dispatch(deleteBranchAsync(branchId));
      dispatch(fetchBranchesAsync({ include_inactive: includeInactive }));
    }
  };

  const handleEdit = (branch: Branch) => {
    setEditBranch(branch);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditBranch(null);
    dispatch(fetchBranchesAsync({ include_inactive: includeInactive }));
  };

  // Only owner can manage branches
  if (currentUser?.role !== 'owner') {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Akses ditolak. Hanya Owner yang dapat mengelola cabang.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manajemen Cabang</h1>
          <p className="text-sm text-gray-600 mt-1">
            Kelola data cabang/toko Anda
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <span>➕</span> Tambah Cabang
        </button>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded"
          />
          Tampilkan cabang nonaktif
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Memuat data...</p>
        </div>
      )}

      {/* Branch List */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <div
              key={branch.id}
              className={`border rounded-lg p-4 ${
                branch.is_active
                  ? 'bg-white border-gray-200'
                  : 'bg-gray-50 border-gray-300 opacity-60'
              }`}
            >
              {/* Branch Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">
                    {branch.name}
                  </h3>
                  <p className="text-sm text-gray-500 font-mono">{branch.code}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    branch.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {branch.is_active ? 'Aktif' : 'Nonaktif'}
                </span>
              </div>

              {/* Branch Details */}
              <div className="space-y-2 mb-4 text-sm text-gray-600">
                {branch.address && (
                  <div className="flex gap-2">
                    <span>📍</span>
                    <span>{branch.address}</span>
                  </div>
                )}
                {branch.phone && (
                  <div className="flex gap-2">
                    <span>📞</span>
                    <span>{branch.phone}</span>
                  </div>
                )}
                {branch.printer_paper_width && (
                  <div className="flex gap-2">
                    <span>🖨️</span>
                    <span>Printer: {branch.printer_paper_width}mm</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(branch)}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                >
                  ✏️ Edit
                </button>
                {branch.is_active && (
                  <button
                    onClick={() => handleDelete(branch.id)}
                    className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium"
                  >
                    🗑️ Nonaktifkan
                  </button>
                )}
              </div>
            </div>
          ))}

          {branches.length === 0 && (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">🏪</div>
              <p className="text-gray-500">Belum ada cabang</p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <BranchFormModal
          onClose={handleCloseModal}
          mode="create"
        />
      )}
      {editBranch && (
        <BranchFormModal
          onClose={handleCloseModal}
          mode="edit"
          branch={editBranch}
        />
      )}
    </div>
  );
};

export default BranchList;
